import { Component, inject, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { concatMap, first } from 'rxjs/operators';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { getObjectDeltas } from '../../../utils/object-deltas';
import { BaseComponent } from '../../../components/base/base.component';
import { CompaniesService } from '../../../services/companies.service';
import { CompanySubTask, CompanySubTaskGroup } from '../../../models/company-subtask';
import { ClientNote } from '../../../models/client';
import { ModalService } from '../../../services/modal.service';
import { ModalStates } from '../../../models/modal';
import { AuditService } from '../../../services/audit.service';
import { ClientSetup } from '../../../models/client';
import { ToastService } from '../../../services/toast.service';
import { ClientTimeclockService } from '../../../services/client-timeclock.service';
import { HttpResponseBase } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../state/ClientSelectionState/ClientSelection.state';

@Component({
  selector: 'company-home',
  templateUrl: './company-home.component.html',
  styleUrls: ['./company-home.component.css'],
  standalone: false
})
export class CompanyHomeComponent extends BaseComponent implements OnInit {
  public clientCode: string;
  public maxPos: number = 1;
  public siteId: number;
  public subTaskForm: UntypedFormGroup;
  public subTasks: CompanySubTask[] = [];
  public apSubTasks: CompanySubTask[] = [];
  public bpSubTasks: CompanySubTask[] = [];
  public clientNote: ClientNote;
  public isMappedToSwipeclock: boolean = false;
  public isWhitelisted: boolean = false;
  public userWhitelistValue: boolean = false;
  public clientSetup: ClientSetup;
  public showWebclock: boolean = false;
  public disabled: boolean = false;

  private readonly toastService = inject(ToastService);

  constructor(
    private auditService: AuditService,
    private companyService: CompaniesService,
    private formBuilder: UntypedFormBuilder,
    private modalService: ModalService,
    private ngModalService: NgbModal,
    private route: ActivatedRoute,
    private clientTimeclockService: ClientTimeclockService,
    private store: Store
  ) {
    super();
    this.subTaskForm = this.formBuilder.group({
      TaskGroupId: new UntypedFormControl('', Validators.required),
      Position: new UntypedFormControl('', [Validators.required, Validators.min(1)]),
      Description: new UntypedFormControl('', Validators.required)
    });
  }

  get form() {
    return this.subTaskForm.controls;
  }

  ngOnInit(): void {
    const obs: Observable<any>[] = [
      this.store.selectOnce(ClientSelectionState.clientCode),
      this.store.selectOnce(ClientSelectionState.clientSetup)
    ];
    forkJoin(obs).subscribe((result: any[]) => {
      this.clientCode = result[0];
      this.clientSetup = result[1];
      //US37082 - Show webclock option for ALL Swipeclock clients, not just ones using REP features.
      // - Elizabeth Sowle 2025-03-20
      this.showWebclock = this.clientSetup.TimeclockVendorId == 'SC';
    });

    this.form.TaskGroupId.valueChanges.subscribe(() => {
      this._setMaxGroupPosition();
      this.form.Position.setValue(this.maxPos, { emitEvent: false });
    });

    this.form.Position.valueChanges.subscribe(() => {
      this._setMaxGroupPosition();
      this._validateGroupPosition();
    });

    this.companyService.getSwipeclockMapping(this.clientCode).subscribe(response => {
      if (response) {
        this.siteId = response;
        this.isMappedToSwipeclock = true;
      }
    });

    this.disabled = true;

    this.companyService.isClientWhitelisted(this.clientCode).subscribe(response => {
      if (response) {
        this.isWhitelisted = response;
        this.userWhitelistValue = this.isWhitelisted;
      }
    });

    this._init();
  }

  public onChange() {
    if (!this.disabled) {
      this.disabled = true;
    } else {
      this.disabled = false;
    }
  }

  // #region public methods
  public createSubTask(content) {
    this.form.TaskGroupId.setValue('1');
    this.form.Position.setValue(this.maxPos, { emitEvent: false });
    this.ngModalService
      .open(content, {
        size: 'md',
        backdrop: 'static',
        keyboard: false
      })
      .result.then(
        result => {
          // success
          if (this.subTaskForm.valid) {
            const subTask = new CompanySubTask();

            subTask.TaskGroupId = this.form.TaskGroupId.value;
            subTask.Position = this.form.Position.value;
            subTask.Description = this.form.Description.value;
            subTask.ClientCode = this.clientCode;

            this.companyService.createSubTask(this.clientCode, subTask).subscribe(id => {
              // audit
              subTask.Id = id;
              const deltas = getObjectDeltas(null, subTask, []);
              this.auditService.audit(`company-subtask-created`, {
                ...deltas,
                ...{
                  ClientCode: this.clientCode
                }
              });

              this.subTasks.push(subTask);
              this._reorderSubtasks(subTask);
              this._updateAllSubTasks();
            });
          }

          this.subTaskForm.reset();
        },
        () => {
          // dismiss
          this.subTaskForm.reset();
        }
      );
  }

  public createSwipeclockMapping(swipeclockMapping) {
    this.ngModalService
      .open(swipeclockMapping, {
        size: 'md',
        backdrop: 'static',
        keyboard: false
      })
      .result.then(
        result => {
          if (this.siteId) {
            if (
              this.siteId.toString().toLowerCase().trim() == this.clientCode.toLowerCase().trim()
            ) {
              this.modalService.showAlertModal(
                'The site id cannot be the same as the client code.',
                ModalStates.Warning
              );
              return;
            } else if (!parseInt(this.siteId.toString())) {
              this.modalService.showAlertModal(
                'The site id should be a numeric value',
                ModalStates.Warning
              );
              return;
            }
            this.clientTimeclockService
              .validateSiteId(this.clientCode, this.siteId)
              .subscribe(result => {
                if (result) {
                  this.companyService
                    .saveSwipeclockMapping(this.clientCode, this.siteId)
                    .subscribe(() => {
                      this.isMappedToSwipeclock = true;
                    });
                } else {
                  this.modalService.showAlertModal(
                    'The site id is not valid.',
                    ModalStates.Warning
                  );
                }
              });
          } else if (this.isMappedToSwipeclock) {
            this.companyService.deleteSwipeclockMapping(this.clientCode).subscribe(() => {
              this.isMappedToSwipeclock = false;
            });
          }
        },
        () => {
          // dismiss
        }
      );
  }

  public isClientWhitelisted(clientWhitelist) {
    this.ngModalService
      .open(clientWhitelist, {
        size: 'md',
        backdrop: 'static',
        keyboard: false
      })
      .result.then(
        result => {
          if (this.clientCode) {
            if (this.userWhitelistValue == this.isWhitelisted) {
              return;
            }
            if (!this.userWhitelistValue) {
              this.companyService.deleteClientWhitelist(this.clientCode).subscribe(() => {
                this.disabled = true;
                this.isWhitelisted = false;
                this.userWhitelistValue = false;
              });
            } else {
              this.companyService
                .saveClientWhitelist(this.clientCode)
                .pipe(
                  concatMap(() =>
                    this.companyService.createEmployeePortalEligibility(this.clientCode)
                  )
                )
                .subscribe(() => {
                  this.disabled = true;
                  this.isWhitelisted = true;
                  this.userWhitelistValue = true;
                });
            }
          }
        },
        () => {
          // dismiss
        }
      );
  }

  public deleteSubTask(subTask: CompanySubTask) {
    this.modalService.showConfirmModal('Are you sure you would like to delete this Subtask?');
    this.modalService.confirmModalCallback.pipe(first()).subscribe((confirm: boolean) => {
      if (confirm) {
        this.companyService.deleteSubTask(this.clientCode, subTask.Id).subscribe(() => {
          this._getSubTasks();

          // record changes in the audit db
          const deltas = getObjectDeltas(subTask, null, []);
          this.auditService.audit(`company-subtask-deleted`, {
            ...deltas,
            ...{
              ClientCode: this.clientCode
            }
          });
        });
      }
    });
  }

  public dropSubTask(event: CdkDragDrop<CompanySubTask[]>) {
    let changedTask;
    if (event.previousContainer != event.container) {
      changedTask = event.previousContainer.data[event.previousIndex];
    } else if (event.previousIndex != event.currentIndex) {
      changedTask = event.container.data[event.previousIndex];
    }

    if (changedTask) {
      let i = this.subTasks.indexOf(changedTask);

      this.subTasks[i].TaskGroupId = event.container.id == 'drop-list-1' ? 1 : 2;
      this.subTasks[i].Position = event.currentIndex + 1;

      this._reorderSubtasks(this.subTasks[i]);
      this._updateAllSubTasks();
    }
  }

  public editSubTask(content, subTask) {
    this._setMaxGroupPosition();
    const index = this.subTasks.indexOf(subTask);
    this.subTaskForm.patchValue(subTask);
    this.ngModalService
      .open(content, {
        size: 'md',
        backdrop: 'static',
        keyboard: false
      })
      .result.then(
        result => {
          // success
          if (this.subTaskForm.valid) {
            const _subTask = { ...subTask };

            subTask.TaskGroupId = this.form.TaskGroupId.value;
            subTask.Position = this.form.Position.value;
            subTask.Description = this.form.Description.value;
            this.subTasks[index] = subTask;

            this._reorderSubtasks(this.subTasks[index]);
            this._updateAllSubTasks();

            // audit
            const deltas = getObjectDeltas(_subTask, subTask, ['IntegrityHash']);
            if (Object.keys(deltas).length > 0) {
              this.auditService.audit(`company-subtask-updated`, {
                ...deltas,
                ClientCode: this.clientCode,
                SubTaskId: subTask.Id
              });
            }
          }
          this.subTaskForm.reset();
        },
        () => {
          // dismiss
          this.subTaskForm.reset();
        }
      );
  }

  // #endregion

  // #region private methods

  private _init() {
    this._getSubTasks();
    this._getClientNote();
  }

  private _getClientNote() {
    // fetch active client note
    this.companyService.getActiveClientNote(this.clientCode).subscribe(note => {
      this.clientNote = note;
    });
  }

  private _getSubTasks() {
    this.companyService.getSubTasks(this.clientCode).subscribe(response => {
      this.subTasks = response;
      this._reorderSubtasks(null);
      this._resortTasks();
    });
  }

  private _reorderSubtasks(subTask: CompanySubTask) {
    // when a task is created or updated
    // all other tasks positions need to be refreshed.
    // the passed subTask was the one most recently changed
    // and position should not be updated here
    let before = this.subTasks.filter(x => x.TaskGroupId == CompanySubTaskGroup.BeforePayroll);
    before = before.sort((a, b) => {
      if (a.Position == b.Position) {
        // prioritize the most recently changed subTask
        // if there are duplicate positions set
        return a == subTask || b == subTask ? -1 : 1;
      } else {
        return a.Position - b.Position;
      }
    });
    for (let i = 0; i < before.length; i++) {
      before[i].Position = i + 1;
    }

    let after = this.subTasks.filter(x => x.TaskGroupId == CompanySubTaskGroup.AfterPayroll);
    after = after.sort((a, b) => {
      if (a.Position == b.Position) {
        // prioritize the most recently changed subTask
        // if there are duplicate positions set
        return a == subTask || b == subTask ? -1 : 1;
      } else {
        return a.Position - b.Position;
      }
    });
    for (let i = 0; i < after.length; i++) {
      after[i].Position = i + 1;
    }

    this.subTasks = [...before, ...after];
  }

  private _resortTasks() {
    this.apSubTasks = [];
    this.bpSubTasks = [];

    let sort = this.subTasks.sort((a, b) => a.Position - b.Position);

    sort.forEach(x => {
      if (x.TaskGroupId == CompanySubTaskGroup.AfterPayroll) {
        this.apSubTasks.push(x);
      } else if (x.TaskGroupId == CompanySubTaskGroup.BeforePayroll) {
        this.bpSubTasks.push(x);
      }
    });
  }

  private _setMaxGroupPosition() {
    // check the form to see which group is selected
    const subtasks = this.subTasks.filter(x => x.TaskGroupId == this.form.TaskGroupId.value);
    this.maxPos = subtasks ? subtasks.length + 1 : 1;
  }

  private _validateGroupPosition() {
    const val = this.form.Position.value;
    if (val > this.maxPos) {
      this.form.Position.setValue(this.maxPos, { emitEvent: false });
    } else if (val < 1) {
      this.form.Position.setValue(1, { emitEvent: false });
    }
  }

  private _updateAllSubTasks() {
    const obs: Observable<HttpResponseBase>[] = [];
    this.subTasks.forEach(subTask => {
      obs.push(this.companyService.updateSubTask(this.clientCode, subTask));
    });

    forkJoin(obs).subscribe(() => {
      this._getSubTasks();
      this.toastService.show('Save successful');
    });
  }

  // #endregion
}
